import { Component, Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';

export interface FreeTierData {
  /** The free allowance the backend actually enforces. */
  freeLimit?: number;
  /** The paid tier being pitched, when the caller knows it. */
  paidLimit?: number;
  paidName?: string;
  paidPrice?: string;
}

@Component({
  selector: 'free-tier-dialog',
  standalone: true,
  imports: [MatButtonModule, MatDialogModule, CommonModule],
  template: `
    <div class="dialog-container">
      <div class="dialog-header">
        <!-- LIAM Logo -->
        <svg class="logo" width="56" height="56" viewBox="0 0 80 80" fill="none">
          <rect width="80" height="80" rx="18" fill="#f97316"/>
          <path d="M25 18V62H55" stroke="white" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/>
          <circle cx="25" cy="18" r="5" fill="white"/>
          <circle cx="55" cy="62" r="5" fill="white"/>
          <circle cx="40" cy="62" r="3" fill="white" opacity="0.6"/>
          <circle cx="25" cy="40" r="3" fill="white" opacity="0.6"/>
        </svg>
        <h2 class="dialog-title">You're on the Free Plan</h2>
      </div>
      
      <div class="dialog-content">
        <p class="dialog-message">
          You're currently using the <strong>Free plan</strong> with limited memory storage.
        </p>
        
        <div class="free-plan-details">
          <div class="plan-item">
            <span class="check-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </span>
            <span>{{ freeLimit | number }} memories per month</span>
          </div>
          <div class="plan-item">
            <span class="check-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </span>
            <span>Basic features</span>
          </div>
        </div>

        <div class="upgrade-banner">
          <div class="upgrade-header">
            <span class="upgrade-badge">Recommended</span>
            <h3>Upgrade to {{ paidName }} Plan</h3>
          </div>
          <div class="starter-benefits">
            <div class="benefit-item">
              <span class="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 2v20M2 12h20"/>
                </svg>
              </span>
              <div>
                <strong>{{ paidLimit | number }} memories/month</strong>
                <span>{{ multiplier }}× more storage</span>
              </div>
            </div>
            <div class="benefit-item">
              <span class="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 6v6l4 2"/>
                </svg>
              </span>
              <div>
                <strong>Never hit limits</strong>
                <span>Chat without interruptions</span>
              </div>
            </div>
            <div class="benefit-item">
              <span class="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </span>
              <div>
                <strong>Richer conversations</strong>
                <span>Better personalized responses</span>
              </div>
            </div>
          </div>
          <div class="price-tag">
            Only <span class="price">\${{ paidPrice }}</span><span class="period">/month</span>
          </div>
        </div>
      </div>

      <div class="dialog-actions">
        <button class="stay-button" (click)="onClose()">
          Stay on Free
        </button>
        <button class="upgrade-button" (click)="onUpgrade()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
          Upgrade Now
        </button>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container {
      font-family: 'Plus Jakarta Sans', 'Inter', system-ui, sans-serif;
      padding: 0;
      background: #ffffff;
      border-radius: 20px;
      overflow: hidden;
      max-width: 440px;
    }

    .dialog-header {
      background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
      color: white;
      padding: 32px 24px;
      text-align: center;
      position: relative;
    }

    .dialog-header::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
      opacity: 0.5;
    }

    .logo {
      margin-bottom: 16px;
      position: relative;
      z-index: 1;
      filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15));
    }

    .dialog-title {
      margin: 0;
      font-size: 24px;
      font-weight: 700;
      color: white;
      position: relative;
      z-index: 1;
    }

    .dialog-content {
      padding: 24px;
    }

    .dialog-message {
      font-size: 15px;
      color: #555;
      margin: 0 0 20px 0;
      text-align: center;
      line-height: 1.5;
    }

    .dialog-message strong {
      color: #333;
    }

    .free-plan-details {
      background: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 12px;
      padding: 16px;
      margin-bottom: 20px;
    }

    .plan-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px 0;
      color: #666;
      font-size: 14px;
    }

    .check-icon {
      width: 22px;
      height: 22px;
      background: rgba(249, 115, 22, 0.1);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .check-icon svg {
      width: 12px;
      height: 12px;
      stroke: #f97316;
    }

    .upgrade-banner {
      background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
      border: 2px solid #f97316;
      border-radius: 16px;
      padding: 20px;
      margin-top: 20px;
      position: relative;
    }

    .upgrade-header {
      text-align: center;
      margin-bottom: 16px;
    }

    .upgrade-badge {
      display: inline-block;
      background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
      color: white;
      font-size: 11px;
      font-weight: 600;
      padding: 4px 12px;
      border-radius: 20px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 8px;
    }

    .upgrade-banner h3 {
      margin: 0;
      font-size: 20px;
      color: #c2410c;
      font-weight: 700;
    }

    .starter-benefits {
      margin-bottom: 16px;
    }

    .benefit-item {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 10px 0;
      border-bottom: 1px solid rgba(249, 115, 22, 0.15);
    }

    .benefit-item:last-child {
      border-bottom: none;
    }

    .benefit-icon {
      width: 32px;
      height: 32px;
      background: rgba(249, 115, 22, 0.15);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .benefit-icon svg {
      width: 16px;
      height: 16px;
      stroke: #f97316;
    }

    .benefit-item div {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .benefit-item strong {
      font-size: 14px;
      color: #333;
      font-weight: 600;
    }

    .benefit-item span {
      font-size: 13px;
      color: #888;
    }

    .price-tag {
      text-align: center;
      font-size: 16px;
      color: #555;
      padding: 14px;
      background: white;
      border-radius: 10px;
      font-weight: 500;
      margin-top: 12px;
      box-shadow: 0 2px 8px rgba(249, 115, 22, 0.1);
    }

    .price {
      font-size: 32px;
      color: #f97316;
      font-weight: 700;
    }

    .period {
      color: #888;
      font-size: 14px;
    }

    .dialog-actions {
      display: flex;
      gap: 12px;
      padding: 20px 24px;
      background: #f8f9fa;
      border-top: 1px solid #e9ecef;
    }

    .stay-button {
      flex: 1;
      background: transparent;
      border: 1px solid #ddd;
      color: #666;
      font-weight: 500;
      font-size: 14px;
      padding: 14px 20px;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .stay-button:hover {
      background: #f1f1f1;
      border-color: #ccc;
    }

    .upgrade-button {
      flex: 2;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
      border: none;
      color: white;
      font-weight: 600;
      font-size: 15px;
      padding: 14px 24px;
      border-radius: 12px;
      cursor: pointer;
      box-shadow: 0 4px 15px rgba(249, 115, 22, 0.35);
      transition: all 0.2s ease;
    }

    .upgrade-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(249, 115, 22, 0.45);
    }

    .upgrade-button svg {
      width: 18px;
      height: 18px;
    }

    @media (max-width: 480px) {
      .dialog-container {
        max-width: 95vw;
        border-radius: 16px;
      }
      
      .dialog-header {
        padding: 24px 20px;
      }
      
      .dialog-title {
        font-size: 20px;
      }
      
      .logo {
        width: 48px;
        height: 48px;
      }

      .dialog-content {
        padding: 20px;
      }

      .dialog-actions {
        flex-direction: column;
        padding: 16px 20px;
      }

      .stay-button, .upgrade-button {
        width: 100%;
      }

      .upgrade-button {
        order: -1;
      }
    }
  `]
})
export class FreeTierDialog {

  /** Defaults are the current plan shape. Every caller that has the live
   *  numbers should pass them - a hardcoded allowance here goes stale the
   *  moment the tier table changes. */
  private static readonly DEFAULT_FREE_LIMIT = 1000;
  private static readonly DEFAULT_PAID_LIMIT = 5000;

  constructor(
    public dialogRef: MatDialogRef<FreeTierDialog>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: FreeTierData | null
  ) { }

  get freeLimit(): number {
    return this.data?.freeLimit ?? FreeTierDialog.DEFAULT_FREE_LIMIT;
  }

  get paidLimit(): number {
    return this.data?.paidLimit ?? FreeTierDialog.DEFAULT_PAID_LIMIT;
  }

  get paidName(): string {
    return this.data?.paidName || 'Personal';
  }

  get paidPrice(): string {
    return this.data?.paidPrice || '9';
  }

  /** "10x more storage" was written when free was 5,000. Derive it instead. */
  get multiplier(): number {
    if (!this.freeLimit) return 0;
    return Math.round(this.paidLimit / this.freeLimit);
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onUpgrade(): void {
    this.dialogRef.close('upgrade');
  }
}